import type { Defesa } from '@/interfaces/defesa';
import defesaService from '@/services/defesa/defesaService';
import { useCursoStore } from '@/stores/CursoStore';
import { useToast } from 'primevue/usetoast';
import { computed, ref } from 'vue';

const defesas = ref<Defesa[]>([]);
const loading = ref(false);
const erro = ref<string | null>(null);

export function useDefesaRevisao() {
  const toast = useToast();
  const cursoStore = useCursoStore();

  const defesasPorCurso = computed(() => {
    if (!cursoStore.cursoAtual?.id) {
      return defesas.value;
    }

    return defesas.value.filter(defesa => {
      const trabalho = defesa.trabalho as any;
      return trabalho?.curso_id === cursoStore.cursoAtual?.id;
    });
  });

  const defesasPendentes = computed(() =>
    defesasPorCurso.value.filter(d => d.status === 'AGUARDANDO_ACEITE_ORIENTADOR')
  );

  const defesasAprovadas = computed(() =>
    defesasPorCurso.value.filter(d => d.status === 'ACEITO_AGUARDANDO_BANCA')
  );

  const defesasAgendadas = computed(() =>
    defesasPorCurso.value.filter(d => d.status === 'AGENDADA')
  );

  const defesasRealizadas = computed(() =>
    defesasPorCurso.value.filter(d => d.status === 'REALIZADA')
  );

  const defesasConcluidas = computed(() =>
    defesasPorCurso.value.filter(d =>
      d.status === 'CONCLUIDA_APROVADO' ||
      d.status === 'CONCLUIDA_APROVADO_COM_CORRECOES' ||
      d.status === 'CONCLUIDA_REPROVADO'
    )
  );

  const defesasCorrecoesPendentes = computed(() =>
    defesasPorCurso.value.filter(d =>
      d.status === 'CONCLUIDA_APROVADO_COM_CORRECOES' &&
      !d.correcoes_aceitas_em
    )
  );

  const defesasFinalizadas = computed(() =>
    defesasPorCurso.value.filter(d =>
      d.status === 'CONCLUIDA_APROVADO' ||
      d.status === 'CONCLUIDA_REPROVADO' ||
      (d.status === 'CONCLUIDA_APROVADO_COM_CORRECOES' && d.correcoes_aceitas_em)
    )
  );

  const totalPendentes = computed(() => defesasPendentes.value.length);

  const totalAprovadas = computed(() => defesasAprovadas.value.length);

  const totalAgendadas = computed(() => defesasAgendadas.value.length);

  const totalRealizadas = computed(() => defesasRealizadas.value.length);

  const totalConcluidas = computed(() => defesasConcluidas.value.length);

  const totalCorrecoesPendentes = computed(() => defesasCorrecoesPendentes.value.length);

  const totalFinalizadas = computed(() => defesasFinalizadas.value.length);

  async function carregarDefesas(filtro?: string) {
    try {
      loading.value = true;
      erro.value = null;

      const params = filtro ? { status: filtro } : undefined;

      const response = await defesaService.listarDefesas(params);

      defesas.value = response.data;
    } catch (err: any) {
      erro.value = err?.response?.data?.message || 'Erro ao carregar defesas';

      toast.add({
        severity: 'error',
        summary: 'Erro ao Carregar',
        detail: erro.value,
        life: 5000
      });

      console.error('❌ Erro ao carregar defesas:', err);
    } finally {
      loading.value = false;
    }
  }

  async function aprovarDefesa(id: number) {
    try {
      await defesaService.aprovarDefesa(id);

      await carregarDefesas();

      toast.add({
        severity: 'success',
        summary: 'Defesa Aprovada',
        detail: 'O aluno foi notificado e poderá agendar a banca.',
        life: 4000
      })
    } catch (err: any) {
      const mensagem = err?.response?.data?.message || 'Erro ao aprovar defesa';

      toast.add({
        severity: 'error',
        summary: 'Erro ao Aprovar',
        detail: mensagem,
        life: 5000
      });
      console.error('❌ Erro ao aprovar defesa:', err);
      throw err;
    }
  }

  async function rejeitarDefesa(id: number, justificativa: string) {
    try {
      if (!justificativa || justificativa.trim().length === 0) {
        throw new Error('Justificativa é obrigatória');
      }

      if (justificativa.trim().length < 20) {
        throw new Error('Justificativa deve ter no mínimo 20 caracteres');
      }

      if (justificativa.length > 2000) {
        throw new Error('Justificativa não pode ter mais de 2000 caracteres');
      }

      await defesaService.rejeitarDefesa(id, justificativa);

      await carregarDefesas();

      toast.add({
        severity: 'info',
        summary: 'Correções Solicitadas',
        detail: 'O aluno foi notificado e poderá fazer as correções necessárias.',
        life: 4000
      });
    } catch (err: any) {
      const mensagem = err?.response?.data?.message || err.message || 'Erro ao rejeitar defesa';

      toast.add({
        severity: 'error',
        summary: 'Erro ao Solicitar Correções',
        detail: mensagem,
        life: 5000
      });
      console.error('❌ Erro ao rejeitar defesa:', err);
      throw err;
    }
  }

  return {
    defesas,
    loading,
    erro,

    defesasPendentes,
    defesasAprovadas,
    defesasAgendadas,
    defesasRealizadas,
    defesasConcluidas,
    defesasCorrecoesPendentes,
    defesasFinalizadas,
    totalPendentes,
    totalAprovadas,
    totalAgendadas,
    totalRealizadas,
    totalConcluidas,
    totalCorrecoesPendentes,
    totalFinalizadas,

    carregarDefesas,
    aprovarDefesa,
    rejeitarDefesa
  };
}
